/**
 * Engine size text — the ONE displacement formatter (founder field test 2026-10-16).
 *
 * NHTSA vPIC reports `Displacement (L)` with arbitrary precision ("2.480000",
 * "2.998832712"). People read engines to one decimal: "2.5L 5 Cyl". The raw
 * decoded value is NOT touched here — it stays in `legacyBasics.displacement`
 * and the profile fact `displacement_l` keeps its own provenance. These helpers
 * only shape the human-facing text.
 *
 * Pure, no I/O, no `https://` imports: shared by the Edge decoder and the browser.
 */

/** A positive numeric displacement in litres, to one decimal ("2.480000" → "2.5", "2" → "2.0"); anything else → null. */
export function formatDisplacementLitres(raw: string | number | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  const text = String(raw).trim();
  if (!/^\d+(?:\.\d+)?$/.test(text)) return null;
  const n = Number(text);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n.toFixed(1);
}

/**
 * The engine text composed from a decode: "2.5L 5 Cyl", or "2.5L" without a cylinder count.
 * A displacement that is not a plain positive number is kept as given (never invented).
 */
export function composeEngineSize(displacement: string | null | undefined, cylinders: string | null | undefined): string | null {
  const raw = (displacement ?? '').trim();
  if (!raw) return null;
  const litres = formatDisplacementLitres(raw) ?? raw;
  const cyl = (cylinders ?? '').trim();
  return cyl ? `${litres}L ${cyl} Cyl` : `${litres}L`;
}

const OVER_PRECISE_LEADING_LITRES = /^(\s*)(\d+\.\d{2,})(\s*)L/i;

/**
 * Display form of an engine text that may already be stored ("2.480000L 5 Cyl" → "2.5L 5 Cyl").
 * Only a LEADING litre figure with two or more decimals is rounded; the cylinder count and
 * every other descriptor are kept exactly. "3.6L V6", "2L 4 Cyl", "Electric" and empty text
 * pass through unchanged. Stored values are never rewritten — this is presentation only.
 */
export function formatEngineSizeDisplay(text: string | null | undefined): string {
  if (!text) return '';
  return text.replace(OVER_PRECISE_LEADING_LITRES, (whole, lead: string, num: string, gap: string) => {
    const n = Number(num);
    if (!Number.isFinite(n) || n <= 0) return whole;
    return `${lead}${n.toFixed(1)}${gap}${whole.slice(-1)}`;
  });
}
